import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Download, Loader2, Printer, ShieldCheck, Snowflake } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { api, errMsg } from "@/lib/api";
import { useMediaQuery } from "@/hooks/use-media-query";
import { QuoteView } from "@/components/canvas/QuoteView";
import { ago } from "@/lib/sk";

async function download(url, name) {
  try {
    const r = await api.get(url, { responseType: "blob" });
    const obj = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = obj; a.download = name; a.click();
    setTimeout(() => URL.revokeObjectURL(obj), 60_000);
  } catch (e) {
    toast.error(errMsg(e));
  }
}

/**
 * /p/:id/tilbud/:rev – the customer document as a web page of its own (print-ready), inside the logged-in workspace.
 * `rev` is a frozen revision id or «live» (today's calculation). Same viewmodel as HTML/PDF; nothing internal is sent.
 */
export default function TilbudPage() {
  const { id, rev } = useParams();
  const [q] = useSearchParams();
  const wide = useMediaQuery("(min-width: 640px)");
  const [data, setData] = useState(null);
  const [revData, setRevData] = useState(null);
  const [err, setErr] = useState(null);
  const live = rev === "live";
  useEffect(() => {
    api.get(`/projects/${id}/tilbud`).then((r) => setData(r.data)).catch((e) => setErr(errMsg(e)));
    if (!live) api.get(`/projects/${id}/tilbud/${rev}`).then((r) => setRevData(r.data)).catch((e) => setErr(errMsg(e)));
  }, [id, rev, live]);
  useEffect(() => { if (q.get("print") === "1" && (live ? data : revData)) setTimeout(() => window.print(), 600); }, [q, data, revData, live]);
  const current = useMemo(() => (data?.revisions || []).find((r) => r.id === rev), [data, rev]);
  const vm = live ? data?.live : revData?.revision?.viewmodel;
  const status = live ? "utkast" : revData?.revision?.status || current?.status || "utkast";
  useEffect(() => { if (vm?.case?.number) document.title = `Tilbud ${vm.case.number}${vm.revision ? ` rev. ${vm.revision}` : ""} – ${vm.case.name}`; }, [vm]);
  const number = vm?.case?.number;
  return (
    <div className="min-h-screen bg-[hsl(210_22%_95%)] print:bg-white" data-testid="tilbud-page" data-view={live ? "live" : "revision"}>
      <header className="sticky top-0 z-10 flex h-12 items-center gap-3 border-b border-black/[0.06] bg-white/90 px-3 backdrop-blur print:hidden sm:px-5" data-testid="tilbud-page-toolbar">
        <Link to={`/p/${id}?tab=tilbud`} className="inline-flex h-8 items-center gap-1.5 rounded-full px-2.5 text-[12.5px] text-foreground/70 hover:bg-black/[0.05] hover:text-foreground" data-testid="tilbud-page-back"><ArrowLeft className="h-3.5 w-3.5" /> Saken</Link>
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-0.5 text-[12.5px] text-muted-foreground">
          <span className="font-medium text-foreground" data-testid="tilbud-page-title">Tilbud {number || "…"}{vm?.revision ? ` · rev. ${vm.revision}` : ""}</span>
          {live
            ? <span data-testid="tilbud-page-live">Gjeldende kalkyle – ikke låst</span>
            : <span className="inline-flex items-center gap-1" data-testid="tilbud-page-rev">{status === "kontrollert" ? <ShieldCheck className="h-3 w-3 text-[hsl(var(--v-ok))]" /> : <Snowflake className="h-3 w-3" />} {current?.status_label || status}{current?.created_at ? ` · opprettet ${ago(current.created_at)}` : ""}</span>}
          <span className="hidden sm:inline">Kundevisning – kost, DB og interne notater finnes ikke her</span>
        </div>
        <button type="button" onClick={() => window.print()} className="inline-flex h-8 items-center gap-1.5 rounded-full px-2.5 text-[12.5px] font-medium text-foreground/80 hover:bg-black/[0.05]" data-testid="tilbud-page-print"><Printer className="h-3.5 w-3.5" /> Skriv ut</button>
        {!live ? <button type="button" onClick={() => download(`/projects/${id}/tilbud/${rev}/pdf`, `Tilbud ${number} rev ${vm?.revision}.pdf`)} className="inline-flex h-8 items-center gap-1.5 rounded-full border border-black/[0.1] bg-white px-2.5 text-[12.5px] font-medium hover:border-black/[0.25]" data-testid="tilbud-page-pdf"><Download className="h-3.5 w-3.5" /> PDF</button> : null}
      </header>
      <main className="px-2 py-6 sm:px-6 sm:py-10 print:p-0">
        {err ? <p className="mx-auto max-w-[860px] text-[13px] text-[hsl(var(--v-blocked))]" data-testid="tilbud-page-error">{err}</p>
          : !vm ? <div className="flex h-60 items-center justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
            : (
              <div className={cn("mx-auto max-w-[940px] rounded-[18px] bg-white pb-10 shadow-[0_1px_2px_hsl(212_40%_16%/0.06),0_24px_60px_-30px_hsl(212_40%_16%/0.35)] print:max-w-none print:rounded-none print:shadow-none")} data-testid="tilbud-page-paper">
                <QuoteView vm={vm} wide={wide} status={status} customerView />
              </div>
            )}
      </main>
    </div>
  );
}
